import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, ShieldCheck, Zap, Lock, Users, Car, Building2, FileText, Info, CheckCircle2, Wrench, Gauge, ClipboardCheck, CalendarCheck, Truck, LineChart, Apple, Play, Mail, MapPin, Clock, Send, Camera } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import VinScannerModal from "../components/VinScannerModal";

const HERO_IMG = "/hero-main.jpg";

const HERO_BADGES = [
  { Icon: ShieldCheck, label: "Güvenilir Bilgi" },
  { Icon: Zap, label: "Hızlı Sorgulama" },
  { Icon: Lock, label: "Ücretsiz Kayıt" },
  { Icon: Users, label: "Bireysel Kullanımda Her Zaman Ücretsiz" },
];

const STATS = [
  { Icon: Car, value: "245.781", label: "Kayıtlı Araç Sayısı" },
  { Icon: Building2, value: "3.642", label: "Kayıtlı İşletme Sayısı" },
  { Icon: Users, value: "128.906", label: "Kayıtlı Üye Sayısı" },
  { Icon: FileText, value: "1.250.000+", label: "Sorgulama İşlemi" },
  { Icon: ShieldCheck, value: "%98", label: "Kullanıcı Memnuniyeti" },
];

const EXPERTISE_LEGEND = [
  { color: "#059669", label: "Orijinal" },
  { color: "#1D4ED8", label: "Boyalı" },
  { color: "#DC2626", label: "Değişen" },
  { color: "#F97316", label: "Lokal Boyalı" },
];

const MAINTENANCE_SAMPLE = [
  { date: "15.06.2025", km: "68.250 km", title: "Periyodik Bakım", desc: "Motor yağı, yağ filtresi, hava filtresi değişimi" },
  { date: "12.01.2024", km: "54.300 km", title: "Fren Sistemi Kontrolü", desc: "Fren balataları değişimi" },
  { date: "03.08.2023", km: "42.150 km", title: "Periyodik Bakım", desc: "Motor yağı, polen filtresi değişimi" },
];

const SERVICE_GRID = [
  { Icon: FileText, label: "Hasar Kaydı (Tramer)" },
  { Icon: Gauge, label: "Kilometre Sorgulama" },
  { Icon: Wrench, label: "Ekspertiz Raporu" },
  { Icon: CalendarCheck, label: "Bakım Geçmişi" },
  { Icon: ShieldCheck, label: "Sigorta / Kasko" },
  { Icon: Truck, label: "Çekici Hizmeti" },
];

const card = "bg-white rounded-2xl border border-slate-200 shadow-sm";

function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const send = async (e) => {
    e?.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/iletisim", { name: form.name.trim(), email: form.email.trim(), message: form.message.trim() });
      setDone(true);
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Mesaj gönderilemedi. Lütfen tekrar deneyin.");
    }
    setBusy(false);
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-sm text-[#0B1B33] placeholder:text-slate-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all";

  return (
    <section id="iletisim" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
      <div className={`${card} p-6 sm:p-10 grid grid-cols-1 lg:grid-cols-2 gap-8`}>
        <div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0B1B33]">
            Bize <span className="text-orange-500">Ulaşın</span>
          </h2>
          <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-md">
            Soru, öneri veya iş birliği talepleriniz için bize yazın; ekibimiz en kısa sürede size dönüş yapar.
          </p>
          <div className="mt-6 space-y-4">
            {[
              { Icon: Mail, title: "E-posta", desc: "info@otokunye.com" },
              { Icon: MapPin, title: "Konum", desc: "Türkiye — tüm illerde hizmet" },
              { Icon: Clock, title: "Yanıt Süresi", desc: "Mesai günlerinde 24 saat içinde" },
            ].map((c) => (
              <div key={c.title} data-testid={`contact-${c.title}`} className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-orange-100 border border-orange-200 flex items-center justify-center shrink-0">
                  <c.Icon className="w-5 h-5 text-orange-600" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-[#0B1B33]">{c.title}</span>
                  <span className="block text-xs text-slate-500">{c.desc}</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {done ? (
          <div data-testid="contact-success" className="flex flex-col items-center justify-center text-center rounded-2xl bg-orange-50 border border-orange-200 p-8">
            <CheckCircle2 className="w-10 h-10 text-orange-500" />
            <p className="mt-3 font-extrabold text-[#0B1B33]">Mesajınız Alındı</p>
            <p className="mt-1 text-sm text-slate-600">En kısa sürede size dönüş yapacağız.</p>
          </div>
        ) : (
          <form onSubmit={send} className="space-y-3">
            <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Adınız Soyadınız" className={inputCls} />
            <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="E-posta adresiniz" className={inputCls} />
            <textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Mesajınız..." rows={4} className={`${inputCls} resize-none`} />
            {err && <p data-testid="contact-error" className="text-sm text-red-600">{err}</p>}
            <button data-testid="contact-submit" type="submit" disabled={busy}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-orange-500 text-white font-bold hover:bg-orange-400 active:scale-[0.99] shadow-[0_8px_20px_rgba(249,115,22,0.3)] transition-all disabled:opacity-60">
              <Send className="w-4 h-4" /> {busy ? "Gönderiliyor..." : "Mesajı Gönder"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  const { user } = useAuth();
  const isBusiness = !!user && (user.role === "servis" || user.role === "cekici");
  const [vin, setVin] = useState("");
  const [error, setError] = useState("");
  const [busy] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    const v = vin.trim().toUpperCase().replace(/\s/g, "");
    if (v.length < 6) {
      setError("Lütfen geçerli bir şasi numarası girin (en az 6 karakter).");
      return;
    }
    navigate(`/rapor/${v}`);
  };

  return (
    <div className="bg-[#F4F8FB]">
      {/* HERO */}
      <section className="pt-24 pb-8 sm:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Sol — başlık + sorgu */}
          <div className="animate-fade-up">
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight leading-[1.08] text-[#0B1B33]">
              Bir Aracın Tüm Geçmişi
              <span className="block text-orange-500">Tek Yerde!</span>
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-md">
              Aracınızı satın almadan önce bilin.<br />Doğru bilgi, güvenli yolculuk.
            </p>

            <div className="mt-7 max-w-lg">
              <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 flex items-center gap-2.5 bg-white border border-slate-300 rounded-xl pl-2 pr-4 focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-100 transition-all">
                <button type="button" data-testid="vin-camera-button" onClick={() => setShowScanner(true)} aria-label="Kamera ile VIN tara"
                  className="p-2.5 rounded-lg text-orange-500 hover:bg-orange-50 active:scale-95 transition-all shrink-0">
                  <Camera className="w-5 h-5" />
                </button>
                <input
                  data-testid="vin-input"
                  value={vin}
                  onChange={(e) => { setVin(e.target.value.toUpperCase()); setError(""); }}
                  placeholder="Şasi (VIN) numaranızı girin"
                  className="w-full bg-transparent py-3.5 text-[#0B1B33] placeholder:text-slate-400 focus:outline-none text-sm sm:text-base"
                />
              </div>
              <button data-testid="vin-search-button" type="submit" disabled={busy}
                className="flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-orange-500 text-white font-bold hover:bg-orange-400 active:scale-[0.98] shadow-[0_8px_20px_rgba(249,115,22,0.35)] transition-all whitespace-nowrap disabled:opacity-60">
                <Search className="w-5 h-5" /> {busy ? "Sorgulanıyor..." : "Sorgula"}
              </button>
            </form>
            </div>
            {error && <p data-testid="vin-input-error" className="mt-3 text-sm text-red-600">{error}</p>}
            {showScanner && (
              <VinScannerModal onResult={(v) => setVin(v)} onClose={() => setShowScanner(false)} />
            )}
            <p className="mt-3 flex items-center gap-2 text-xs sm:text-sm text-slate-500">
              <Info className="w-4 h-4 text-orange-500 shrink-0" /> Şasi (VIN) numaranızı girin
            </p>
          </div>

          {/* Sağ — araç görseli + rozetler */}
          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-2xl">
              <img src={HERO_IMG} alt="Otokünye — araç geçmişi" className="w-full h-[300px] sm:h-[380px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B33]/25 via-transparent to-transparent" />
              <span className="absolute top-4 right-4 font-script text-xl sm:text-2xl text-white drop-shadow-[0_2px_8px_rgba(11,27,51,0.7)] rotate-[-3deg]">
                Aracını tanı,<br />geleceğini güvenceye al!
              </span>
            </div>
            <div className="absolute left-3 sm:-left-4 bottom-3 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 flex flex-col gap-2.5">
              {HERO_BADGES.map((b, i) => (
                <div key={b.label} data-testid={`hero-badge-${i}`}
                  className="flex items-center gap-2.5 bg-white/95 backdrop-blur rounded-full pl-1.5 pr-4 py-1.5 shadow-lg border border-slate-200 animate-fade-up"
                  style={{ animationDelay: `${200 + i * 90}ms` }}>
                  <span className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center shrink-0">
                    <b.Icon className="w-4 h-4 text-white" />
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-[#0B1B33] leading-tight">{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* İSTATİSTİK ŞERİDİ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`${card} grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 divide-y sm:divide-y-0 lg:divide-x divide-slate-100`}>
          {STATS.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label}`} className="flex items-center gap-3 px-4 sm:px-6 py-5">
              <s.Icon className="w-8 h-8 text-orange-500 shrink-0" />
              <span>
                <span className="block text-lg sm:text-xl font-extrabold text-[#0B1B33]">{s.value}</span>
                <span className="block text-[11px] sm:text-xs text-slate-500">{s.label}</span>
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ÖZELLİK KARTLARI */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 sm:mt-14 pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">

          {/* 1 — Araç Bilgileri */}
          <div data-testid="feature-arac-bilgileri" className={`${card} p-6 flex flex-col`}>
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-orange-500 text-white text-xl font-extrabold flex items-center justify-center shrink-0">1</span>
              <div>
                <h3 className="font-extrabold text-[#0B1B33]">Araç Bilgileri</h3>
                <p className="text-xs text-slate-500 mt-0.5">Aracın temel bilgilerini görüntüleyin.</p>
              </div>
            </div>
            <div className="mt-5 rounded-xl border border-slate-200 bg-[#F8FBFD] p-4 space-y-2.5 text-xs">
              {[["Model Yılı", "2021"], ["Kilometre", "68.250 km"], ["Yakıt Türü", "Dizel"], ["Şanzıman", "Manuel"], ["Kasa Tipi", "Panelvan"]].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-slate-100 last:border-0 pb-2 last:pb-0">
                  <span className="text-slate-500">{k}</span>
                  <span className="font-bold text-[#0B1B33]">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 2 — Ekspertiz Bilgileri */}
          <div data-testid="feature-ekspertiz" className={`${card} p-6 flex flex-col`}>
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-orange-500 text-white text-xl font-extrabold flex items-center justify-center shrink-0">2</span>
              <div>
                <h3 className="font-extrabold text-[#0B1B33]">Ekspertiz Bilgileri</h3>
                <p className="text-xs text-slate-500 mt-0.5">Aracın detaylı ekspertiz raporunu inceleyin.</p>
              </div>
            </div>
            <div className="mt-5 rounded-xl border border-slate-200 bg-[#F8FBFD] p-4">
              <div className="grid grid-cols-2 gap-2.5 text-xs">
                {EXPERTISE_LEGEND.map((l) => (
                  <span key={l.label} className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: l.color }} />
                    <span className="font-semibold text-[#0B1B33]">{l.label}</span>
                  </span>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="text-[11px] font-semibold text-emerald-700">Ağır hasar kaydı kontrolü</span>
              </div>
            </div>
          </div>

          {/* 3 — Bakım Geçmişi */}
          <div data-testid="feature-bakim" className={`${card} p-6 flex flex-col`}>
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-orange-500 text-white text-xl font-extrabold flex items-center justify-center shrink-0">3</span>
              <div>
                <h3 className="font-extrabold text-[#0B1B33]">Bakım Geçmişi</h3>
                <p className="text-xs text-slate-500 mt-0.5">Aracın servis ve bakım kayıtlarını görün.</p>
              </div>
            </div>
            <div className="mt-5 rounded-xl border border-slate-200 bg-[#F8FBFD] p-4 space-y-3">
              {MAINTENANCE_SAMPLE.map((m) => (
                <div key={m.date + m.title} className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-orange-100 border border-orange-300 flex items-center justify-center shrink-0">
                    <Wrench className="w-3.5 h-3.5 text-orange-600" />
                  </span>
                  <div className="text-xs">
                    <p className="text-slate-400">{m.date} • {m.km}</p>
                    <p className="font-bold text-[#0B1B33]">{m.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 4 — Hizmetlerimiz */}
          <div data-testid="feature-hizmetler" className={`${card} p-6 flex flex-col`}>
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-orange-500 text-white text-xl font-extrabold flex items-center justify-center shrink-0">4</span>
              <div>
                <h3 className="font-extrabold text-[#0B1B33]">Hizmetlerimiz</h3>
                <p className="text-xs text-slate-500 mt-0.5">Tüm ihtiyaçlarınız tek platformda.</p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-2">
              {SERVICE_GRID.map((s) => (
                <Link key={s.label} to="/hizmetler" data-testid={`service-tile-${s.label}`}
                  className="flex flex-col items-center gap-1.5 rounded-xl border border-slate-200 bg-[#F8FBFD] hover:border-orange-400 hover:bg-orange-50/50 px-1 py-3 transition-colors">
                  <s.Icon className="w-5 h-5 text-orange-500" />
                  <span className="text-[9.5px] font-semibold text-[#0B1B33] text-center leading-tight">{s.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Alt slogan + CTA */}
        <div className="mt-12 text-center">
          <p className="font-script text-3xl sm:text-4xl text-[#0B1B33]">
            Doğru Bilgi; <span className="text-orange-500 underline decoration-orange-400 decoration-2 underline-offset-4">Daha Güvenli Yollar</span>
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            {!isBusiness && (
            <Link data-testid="register-my-vehicle-button" to="/arac-kaydet"
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#0B1B33] on-img font-bold hover:bg-[#12294A] transition-colors">
              <Car className="w-5 h-5 text-orange-400" /> Aracımı Kaydet
            </Link>
            )}
            <Link data-testid="home-teklif-al-button" to="/teklif-al"
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl bg-orange-500 text-white font-bold hover:bg-orange-400 shadow-[0_8px_20px_rgba(249,115,22,0.35)] transition-all">
              <ClipboardCheck className="w-5 h-5" /> İşletmelerden Teklif Al
            </Link>
            <button data-testid="cta-start-button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl border border-slate-300 text-[#0B1B33] font-bold hover:border-orange-400 hover:text-orange-600 transition-colors">
              <LineChart className="w-5 h-5" /> Hemen Sorgula
            </button>
          </div>
        </div>
      </section>

      {/* İLETİŞİM */}
      <ContactSection />

      {/* FOOTER — marka mesajı + mağaza rozetleri */}
      <footer className="border-t border-slate-200 bg-white mt-12 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-7 h-7 text-orange-500" />
              <span className="font-heading font-extrabold text-2xl text-[#0B1B33]">OTO<span className="text-orange-500">KÜNYE</span></span>
            </div>
            <p data-testid="home-brand-message" className="text-sm text-slate-600 border-l border-slate-300 pl-4">
              Her Aracın Bir Geçmişi Vardır,<br />
              <span className="text-orange-600 font-semibold">Biz O Geçmişi Sizin İçin Aydınlatırız.</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="appstore-badge" onClick={() => toast.info("Mobil uygulama yakında!")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0B1B33] hover:bg-[#12294A] transition-colors">
              <Apple className="w-5 h-5 on-img" />
              <span className="text-left leading-tight">
                <span className="block text-[9px] on-img-soft">App Store'dan</span>
                <span className="block text-xs font-bold on-img">İndirin</span>
              </span>
            </button>
            <button data-testid="googleplay-badge" onClick={() => toast.info("Mobil uygulama yakında!")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0B1B33] hover:bg-[#12294A] transition-colors">
              <Play className="w-5 h-5 on-img" />
              <span className="text-left leading-tight">
                <span className="block text-[9px] on-img-soft">Google Play</span>
                <span className="block text-xs font-bold on-img">Den Alın</span>
              </span>
            </button>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-slate-500">
          <a data-testid="footer-privacy-link" href="/gizlilik-politikasi" className="hover:text-orange-600 transition-colors underline underline-offset-2">Gizlilik Politikası</a>
          {" • "}© 2026 OTO KÜNYE
        </p>
      </footer>
    </div>
  );
}
